import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { HaModalService } from "@ha/ui/common";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

import { FlammableMaterial } from "../../../models/customer-permit-template.model";
import { FlammableMaterialMeasurement } from "../../../models/question.model";
import {
    FlammableMaterialInfoModalComponent,
    FlammableMaterialInfoModalComponentData,
} from "../flammable-material-info-modal/flammable-material-info-modal.component";

@UntilDestroy()
@Component({
    selector: "pmt-substance-measurement",
    templateUrl: "substance-measurement.component.html",
    styleUrls: ["substance-measurement.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubstanceMeasurementComponent implements OnChanges {
    @Input()
    public materials: FlammableMaterial[] = [];

    @Input()
    public savedMeasurements: FlammableMaterialMeasurement[] | null;

    @Output()
    public measurementsChange = new EventEmitter<FlammableMaterialMeasurement[]>();

    @Output()
    public validChange = new EventEmitter<boolean>();

    public form: FormGroup;
    public measurementsArray: FormArray;

    constructor(private modalService: HaModalService) {}

    public ngOnChanges(): void {
        this.buildForm();
    }

    public incrementValue(materialId: number): void {
        const group = this.getMeasurementFormGroup(materialId);
        if (group) {
            const currentValue = (group.get("value")?.value as number) || 0;
            group.get("value")?.setValue(+((currentValue + 0.1).toFixed(3)));
        }
    }

    public decrementValue(materialId: number): void {
        const group = this.getMeasurementFormGroup(materialId);
        if (group) {
            const currentValue = (group.get("value")?.value as number) || 0;
            const newValue = Math.max(0, +((currentValue - 0.1).toFixed(3)));
            group.get("value")?.setValue(newValue);
        }
    }

    public getMeasurementFormGroup(materialId: number): FormGroup | null {
        if (!this.measurementsArray) {
            return null;
        }
        return this.measurementsArray.controls.find(
            (c) => c.get("flammableMaterialId")?.value === materialId,
        ) as FormGroup | null;
    }

    public openMaterialInfo(material: FlammableMaterial): void {
        this.modalService.open(
            FlammableMaterialInfoModalComponent,
            true,
            <FlammableMaterialInfoModalComponentData>{ material },
        );
    }

    public isWithinMaxConcentration(material: FlammableMaterial): boolean {
        const value = this.getMeasurementValue(material.flammableMaterialId);
        if (value === null || material.maxConcentration === null || material.maxConcentration === undefined) {
            return false;
        }

        return value <= Number(material.maxConcentration);
    }

    public isAboveMaxConcentration(material: FlammableMaterial): boolean {
        const value = this.getMeasurementValue(material.flammableMaterialId);
        if (value === null || material.maxConcentration === null || material.maxConcentration === undefined) {
            return false;
        }

        return value > Number(material.maxConcentration);
    }

    private buildForm(): void {
        const currentValuesByMaterialId = new Map<number, number | null>(
            (this.measurementsArray?.controls ?? []).map((control) => [
                control.get("flammableMaterialId")?.value as number,
                this.toMeasurementValue(control.get("value")?.value),
            ]),
        );

        this.measurementsArray = new FormArray([]);

        (this.materials || []).forEach((material) => {
            const savedValue = this.savedMeasurements?.find(
                (m) => m.flammableMaterialId === material.flammableMaterialId,
            );
            const currentValue = currentValuesByMaterialId.get(material.flammableMaterialId);

            this.measurementsArray.push(
                new FormGroup({
                    flammableMaterialId: new FormControl(material.flammableMaterialId),
                    value: new FormControl(currentValue ?? savedValue?.value ?? null, [Validators.required]),
                }),
            );
        });

        this.form = new FormGroup({
            measurements: this.measurementsArray,
        });

        this.measurementsArray.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            this.emitState();
        });

        this.emitState();
    }

    private emitState(): void {
        const measurements: FlammableMaterialMeasurement[] = this.measurementsArray.controls
            .filter((c) => {
                const value = c.get("value")?.value as number | null;
                return value !== null && value !== undefined && value !== ("" as unknown);
            })
            .map((c) => ({
                flammableMaterialId: c.get("flammableMaterialId")?.value as number,
                value: c.get("value")?.value as number,
            }));

        this.measurementsChange.emit(measurements);

        const valid = this.measurementsArray.controls.every((c) => {
            const value = c.get("value")?.value as number | null;
            return value !== null && value !== undefined && value !== ("" as unknown);
        });
        this.validChange.emit(valid);
    }

    private getMeasurementValue(materialId: number): number | null {
        const group = this.getMeasurementFormGroup(materialId);

        return this.toMeasurementValue(group?.get("value")?.value);
    }

    private toMeasurementValue(value: unknown): number | null {
        if (value === null || value === undefined || value === "") {
            return null;
        }

        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? null : numericValue;
    }
}
